import './FinancialFilters.scss'
import React from "react";
import {observer} from "mobx-react-lite";
import {
    Autocomplete,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import {Apartment, Category as CategoryIcon, Layers, Public} from "@mui/icons-material";
import {useSitStores} from "~/stores";
import type {CategoryLabel, Cluster, Company, LeverGroup, ValueType} from "../../ApiTypes";
import {DOIs} from "../../ApiTypes";
import {sitCountTags} from "../../components/design/SitDesignAtoms";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers-pro";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {stageGateLabel} from "~/sit/sitLabels.ts";
import i18n from "../../../i18n/i18n";

const fin = (k: string) => i18n.t(`fin.${k}`, {ns: 'sit'});

// Prepend a leading icon to an Autocomplete's input, keeping its own adornment (tags).
const withIcon = (icon: React.ReactNode, params: React.ComponentProps<typeof TextField>['InputProps']) => ({
    ...params,
    startAdornment: <><InputAdornment position="start">{icon}</InputAdornment>{params?.startAdornment}</>,
});

export const FinancialFilters: React.FC = observer(() => {
    const {ccStore, fbStore} = useSitStores();

    return (
        <div className="financial-filters-component sit-filterbar">
            <FormControl size="small" className="sitf sit-filter-select sit-filter-year">
                <span className="sitf-label">{fin('analysisYear')}</span>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        value={dayjs(new Date(fbStore.year, 0))}
                        onChange={(value) => {
                            if (value) fbStore.setYear(value.toDate().getFullYear());
                        }}
                        views={['year']}
                        slotProps={{textField: {size: 'small'}}}/>
                </LocalizationProvider>
            </FormControl>

            <FormControl size="small" className="sitf sit-filter-select">
                <span className="sitf-label">{fin('savings')}</span>
                <Select value={fbStore.activeValueType}
                        onChange={(event) => fbStore.setActiveValueType(event.target.value as ValueType)}>
                    <MenuItem value="rolling">{fin('rolling')}</MenuItem>
                    <MenuItem value="fiscal_year">{fin('fiscalYear')}</MenuItem>
                </Select>
            </FormControl>

            <FormControlLabel
                className="sit-filter-check"
                control={<Checkbox size="small" checked={fbStore.probabilityAdjusted}
                                   onChange={event => fbStore.setProbabilityAdjusted(event.target.checked)}/>}
                label={fin('probabilityAdjusted')}/>

            <label className="sitf sit-filter-auto">
                <span className="sitf-label">{fin('cluster')}</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={ccStore.clusterRM.result ?? ([] as Cluster[])}
                    getOptionLabel={(option) => option.title}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={fbStore.combinedClusters}
                    renderInput={(params) =>
                        <TextField {...params}
                                   placeholder={fbStore.combinedClusters.length ? fin('search') : fin('all')}
                                   InputProps={withIcon(<Layers fontSize="small"/>, params.InputProps)}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option.id}>
                            <Checkbox checked={fbStore.isCheckedClusters(option.id)}
                                      indeterminate={fbStore.isIndeterminateCluster(option.id)}/>
                            {option.title}
                        </li>}
                    onChange={(event, value, reason) => fbStore.setClusters(value, reason === 'removeOption')}/>
            </label>

            <label className="sitf sit-filter-auto">
                <span className="sitf-label">{fin('company')}</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={(ccStore.companyRM.result ?? []) as Company[]}
                    getOptionLabel={(option) => option.title}
                    value={fbStore.activeFilteredCompanies}
                    renderInput={(params) =>
                        <TextField {...params}
                                   placeholder={fbStore.activeFilteredCompanyIds.length ? fin('search') : fin('all')}
                                   InputProps={withIcon(<Apartment fontSize="small"/>, params.InputProps)}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option.id}>
                            <Checkbox checked={(fbStore.activeFilteredCompanyIds?.indexOf(option.id) ?? -1) > -1}/>
                            {option.title}
                        </li>}
                    onChange={(event, value) => fbStore.setActiveFilteredCompanies(value.map(c => c.id))}/>
            </label>

            <FormControl size="small" className="sitf sit-filter-select">
                <span className="sitf-label">{fin('scope')}</span>
                <Select value={fbStore.scope} displayEmpty
                        startAdornment={<InputAdornment position="start"><Public fontSize="small"/></InputAdornment>}
                        onChange={(event) => fbStore.setScope(event.target.value as 'local' | 'global')}>
                    <MenuItem value="">{fin('all')}</MenuItem>
                    <MenuItem value="local">{fin('local')}</MenuItem>
                    <MenuItem value="global">{fin('global')}</MenuItem>
                </Select>
            </FormControl>

            <FormControl size="small" className="sitf sit-filter-select">
                <span className="sitf-label">{fin('stage')}</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={DOIs}
                    getOptionLabel={(option) => stageGateLabel(option)}
                    value={fbStore.dois}
                    renderInput={(params) =>
                        <TextField {...params} placeholder={fbStore.dois.length ? fin('search') : fin('all')}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option} style={{whiteSpace: 'nowrap'}}>
                            <Checkbox checked={fbStore.dois.includes(option)}/>
                            {stageGateLabel(option)}
                        </li>}
                    onChange={(_event, value) => fbStore.setDois(value)}/>
            </FormControl>

            <label className="sitf sit-filter-auto">
                <span className="sitf-label">{fin('leverGroups')}</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    disabled={!ccStore.leverGroupRM.result}
                    options={ccStore.leverGroupRM.result ?? ([] as LeverGroup[])}
                    getOptionLabel={(option) => option.title}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={fbStore.activeLeverGroups}
                    renderInput={(params) =>
                        <TextField {...params}
                                   placeholder={fbStore.activeLeverGroups.length ? fin('search') : fin('all')}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option.id}>
                            <Checkbox checked={fbStore.activeLeverGroups.findIndex(l => l.id === option.id) > -1}/>
                            {option.title}
                        </li>}
                    onChange={(_event, value) => fbStore.setActiveLeverGroupIds(value)}/>
            </label>

            <label className="sitf sit-filter-auto">
                <span className="sitf-label">{fin('category')}</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    disabled={!ccStore.allCategories}
                    options={(ccStore.allCategories ?? []) as CategoryLabel[]}
                    getOptionLabel={(category) => category.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={fbStore.activeCategories}
                    renderInput={(params) =>
                        <TextField {...params} placeholder={fin('select')}
                                   InputProps={withIcon(<CategoryIcon fontSize="small"/>, params.InputProps)}/>}
                    renderOption={(props, category) =>
                        <li {...props} key={category.id}>
                            <Checkbox checked={fbStore.activeCategories.findIndex(c => c.id === category.id) !== -1}/>
                            {category.label}
                        </li>}
                    onChange={(event, value, reason) => fbStore.onChangeActiveCategory(value, reason)}/>
            </label>

            <FormControl size="small" className="sitf sit-filter-select">
                <span className="sitf-label">{fin('approval')}</span>
                <Select value={fbStore.approved} displayEmpty
                        onChange={(event) => fbStore.setApproved(event.target.value as '' | 'approved' | 'pending')}>
                    <MenuItem value="">{fin('all')}</MenuItem>
                    <MenuItem value="approved">{fin('approved')}</MenuItem>
                    <MenuItem value="pending">{fin('pending')}</MenuItem>
                </Select>
            </FormControl>
        </div>
    );
});
